import React, { useMemo, useState, useRef } from 'react';
import '../styles/RedfinMarketTrends.css';
import { format, parse } from 'date-fns';
import { colour } from '../styles/GlobalStyles';
import Badge from './Badge';
import RedirectArrow from '../assets/icons/RedirectArrow';
import Tooltip from './Tooltip';
import SimpleLineChart from './SimpleLineChart';

function RedfinMarketTrends(props) {
    const redfinMarketTrends = props.redfinMarketTrends;
    const darkMode = props.darkMode || false;
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState(null);
    const containerRef = useRef(null);

    // Process chart data
    const chartData = useMemo(() => {
        if (!redfinMarketTrends || !redfinMarketTrends.last12Months || !Array.isArray(redfinMarketTrends.last12Months)) {
            return [];
        }

        return redfinMarketTrends.last12Months.map(month => ({
            date: format(new Date(month.periodEnd), "MMM yyyy"),
            price: month.medianSalePrice,
            volume: month.homesSold,
            dom: month.medianDaysOnMarket
        }));
    }, [redfinMarketTrends]);

    // Extract analysis text
    const getAnalysisText = () => {
        if (!redfinMarketTrends || !redfinMarketTrends.analysis) return '';
        
        const analysis = redfinMarketTrends.analysis;
        if (typeof analysis === 'string') {
            return analysis;
        } else if (analysis && typeof analysis === 'object') {
            if (analysis.text) {
                return analysis.text;
            } else if (analysis.content) {
                return analysis.content;
            } else if (analysis.analysis && typeof analysis.analysis === 'string') {
                return analysis.analysis;
            } else {
                const keys = Object.keys(analysis);
                if (keys.length === 1 && typeof analysis[keys[0]] === 'string') {
                    return analysis[keys[0]];
                }
            }
        }
        return String(analysis || '');
    };

    // Extract recommendation text from Firebase
    const getRecommendationText = () => {
        if (!redfinMarketTrends || !redfinMarketTrends.recommendation) return '';
        
        const recommendation = redfinMarketTrends.recommendation;
        if (typeof recommendation === 'string') {
            return recommendation;
        } else if (recommendation && typeof recommendation === 'object') {
            if (recommendation.text) {
                return recommendation.text;
            } else if (recommendation.content) {
                return recommendation.content;
            } else if (recommendation.recommendation && typeof recommendation.recommendation === 'string') {
                return recommendation.recommendation;
            } else {
                const keys = Object.keys(recommendation);
                if (keys.length === 1 && typeof recommendation[keys[0]] === 'string') {
                    return recommendation[keys[0]];
                }
            }
        }
        return String(recommendation || '');
    };

    // Format inventory badge props
    const getInventoryBadgeProps = (inventory) => {
        if (!inventory) return null;
        
        const inventoryLower = String(inventory).toLowerCase();
        if (inventoryLower === 'high') {
            return {
                bgColour: 'bg-colour-green-01',
                statusDotColour: colour.green02,
                text: 'High inventory'
            };
        } else if (inventoryLower === 'moderate' || inventoryLower === 'medium') {
            return {
                bgColour: 'bg-colour-yellow-01',
                statusDotColour: colour.yellow02,
                text: 'Moderate inventory'
            };
        } else if (inventoryLower === 'low') {
            return {
                bgColour: 'bg-colour-red-01',
                statusDotColour: colour.red02,
                text: 'Low inventory'
            };
        }
        return null;
    };

    if (!redfinMarketTrends || !redfinMarketTrends.last12Months || chartData.length === 0) {
        return null;
    }

    const textColorClass = darkMode ? 'colour-white' : 'colour-primary';
    const containerClass = darkMode ? 'bg-colour-dark-mode' : 'bg-colour-white';
    const textButtonClass = darkMode ? 'text-button-inverse' : 'text-button';
    const arrowFill = darkMode ? colour.gray00 : colour.gray06;
    const zipcode = redfinMarketTrends.zipcode;
    const redfinSourceUrl = zipcode ? `https://www.redfin.com/zipcode/${zipcode}/housing-market` : null;

    const inventoryBadgeProps = redfinMarketTrends.inventory ? getInventoryBadgeProps(redfinMarketTrends.inventory) : null;
    const analysisText = getAnalysisText();
    const recommendationText = getRecommendationText();

    return (
        <div className={`redfin-market-trends-container ${containerClass}`} ref={containerRef}>
            {/* Header Section */}
            <div className="redfin-market-trends-header-section">
                <h3 className={`heading-small-bold ${textColorClass}`}>
                    Market analysis
                </h3>
                {analysisText && (
                    <p className={`body-regular ${textColorClass}`}>
                        {analysisText.replace(/\*\*/g, '')}
                    </p>
                )}
            </div>

            {/* Chart Section */}
            <div className="redfin-market-trends-chart-wrapper">
                <SimpleLineChart 
                    data={chartData} 
                    darkMode={darkMode}
                    trendBucket={redfinMarketTrends.trendBucket}
                    onPointHover={(index, position) => {
                        setHoveredIndex(index);
                        setTooltipPosition(position);
                    }}
                    onPointLeave={() => {
                        setHoveredIndex(null);
                        setTooltipPosition(null);
                    }}
                    hoveredIndex={hoveredIndex}
                    tooltipPosition={tooltipPosition}
                />
            </div>
            
            {/* Tooltip rendered at container level to avoid clipping */}
            {hoveredIndex !== null && tooltipPosition && (() => {
                const validData = chartData.filter(d => d.price !== null && d.price !== undefined);
                if (hoveredIndex >= validData.length) return null;
                
                const point = validData[hoveredIndex];
                if (!point) return null;
                
                const currentPrice = validData.length > 0 ? validData[validData.length - 1].price : null;
                const percentageDiff = currentPrice && point.price 
                    ? ((point.price - currentPrice) / currentPrice) * 100 
                    : 0;
                const absPercentage = Math.round(Math.abs(percentageDiff));
                const percentageColoredPart = percentageDiff > 0 
                    ? `${absPercentage}% higher` 
                    : `${absPercentage}% lower`;
                const percentageRest = ' than present';
                const percentageColorClass = percentageDiff > 0 ? 'colour-error' : 'colour-green';
                
                // Calculate months ago (current month is last index)
                const monthsAgo = validData.length - 1 - hoveredIndex;
                
                // Extract abbreviated month name from point.date (format is "MMM yyyy")
                let monthAbbr = '';
                try {
                    const dateObj = parse(point.date, 'MMM yyyy', new Date());
                    if (!isNaN(dateObj.getTime())) {
                        monthAbbr = format(dateObj, 'MMM');
                    } else {
                        // Fallback to abbreviated month if parsing fails
                        monthAbbr = point.date ? point.date.split(' ')[0] : '';
                    }
                } catch (e) {
                    // Fallback to abbreviated month if parsing fails
                    monthAbbr = point.date ? point.date.split(' ')[0] : '';
                }
                
                const monthsAgoText = (
                    <>
                        <span className="body-regular">{monthAbbr}</span>
                        <span> • {monthsAgo === 0 ? 'Current month' : `${monthsAgo} ${monthsAgo === 1 ? 'month' : 'months'} ago`}</span>
                    </>
                );
                
                // Determine if tooltip should be above or below (simplified - can be improved)
                const showAbove = tooltipPosition.y < 150;
                
                // Calculate tooltip bounds to prevent overflow
                // Estimate tooltip width (approximate based on content)
                const fullPercentageText = percentageColoredPart + percentageRest;
                const tooltipWidth = Math.max(200, Math.max(monthsAgoText.length, fullPercentageText.length) * 8 + 40);
                const containerWidth = containerRef.current ? containerRef.current.getBoundingClientRect().width : window.innerWidth;
                
                // Check if tooltip would overflow on the right
                const tooltipRightEdge = tooltipPosition.x + (tooltipWidth / 2);
                const wouldOverflowRight = tooltipRightEdge > containerWidth - 24; // 24px padding
                
                // Adjust position if it would overflow
                let adjustedLeft = tooltipPosition.x;
                let transformClass = showAbove ? 'redfin-market-trends-tooltip-above' : 'redfin-market-trends-tooltip-below';
                
                if (wouldOverflowRight) {
                    // Position from the point's x position, but ensure it doesn't overflow
                    // Calculate how much we need to shift left
                    const overflowAmount = tooltipRightEdge - (containerWidth - 24);
                    adjustedLeft = tooltipPosition.x - overflowAmount;
                    transformClass = showAbove 
                        ? 'redfin-market-trends-tooltip-above-right' 
                        : 'redfin-market-trends-tooltip-below-right';
                }
                
                // Also check left edge
                const tooltipLeftEdge = adjustedLeft - (tooltipWidth / 2);
                const wouldOverflowLeft = tooltipLeftEdge < 24;
                
                if (wouldOverflowLeft && !wouldOverflowRight) {
                    // Calculate how much we need to shift right
                    const underflowAmount = 24 - tooltipLeftEdge;
                    adjustedLeft = tooltipPosition.x + underflowAmount;
                    transformClass = showAbove 
                        ? 'redfin-market-trends-tooltip-above-left' 
                        : 'redfin-market-trends-tooltip-below-left';
                }
                
                return (
                    <div 
                        className={`redfin-market-trends-tooltip-wrapper ${transformClass}`}
                        style={{
                            left: `${adjustedLeft}px`,
                            top: `${tooltipPosition.y}px`
                        }}
                    >
                        <Tooltip
                            type="info"
                            title={monthsAgoText}
                            body={percentageColoredPart + percentageRest}
                            bodyColoredPart={percentageColoredPart}
                            bodyRest={percentageRest}
                            position={showAbove ? "top" : "bottom"}
                            exit={false}
                            customClass=""
                            hideBeak={true}
                            bodyColorClass={percentageColorClass}
                        />
                    </div>
                );
            })()}

            {zipcode && redfinSourceUrl && (
                <div className="redfin-market-trends-source-link">
                    <a
                        href={redfinSourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={`${textButtonClass} redfin-market-trends-source-link-anchor`}
                        aria-label={`View Redfin housing data for ${zipcode}`}
                    >
                        <span className={`body-semibold ${textColorClass}`}>
                            View on Redfin
                        </span>
                        <RedirectArrow
                            fill={arrowFill}
                            className="redfin-market-trends-source-link-icon"
                        />
                    </a>
                </div>
            )}

            {/* Review Section */}
            {(inventoryBadgeProps || zipcode || recommendationText) && (
                <div className="redfin-market-trends-review-section">
                    {(inventoryBadgeProps || zipcode) && (
                        <div className="redfin-market-trends-review-badges">
                            {inventoryBadgeProps && (
                                <Badge
                                    bgColour={inventoryBadgeProps.bgColour}
                                    statusDot={true}
                                    statusDotColour={inventoryBadgeProps.statusDotColour}
                                    text={inventoryBadgeProps.text}
                                    textClass={`body-regular colour-primary`}
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={null}
                                />
                            )}
                            {inventoryBadgeProps && zipcode && (
                                <span className={`body-regular ${textColorClass}`}>
                                    in ZIP code
                                </span>
                            )}
                            {zipcode && (
                                <Badge
                                    bgColour="bg-colour-gray-02"
                                    statusDot={false}
                                    statusDotColour={null}
                                    text={zipcode}
                                    textClass={`body-regular colour-primary`}
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={null}
                                />
                            )}
                        </div>
                    )}
                    {recommendationText && (
                        <p className={`body-regular ${textColorClass}`}>
                            {recommendationText.replace(/\*\*/g, '')}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

export default RedfinMarketTrends;

